import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDollarSign, FiDownload, FiPrinter, FiSearch, FiTrendingUp,
  FiTrendingDown, FiPercent, FiBarChart2, FiChevronDown, FiChevronRight,
  FiExternalLink, FiFileText
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getLocationsCached, getEmployeesCached, getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getGroupKey = (dateStr, groupBy) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  switch (groupBy) {
    case 'daily':
      return dateStr;
    case 'weekly': {
      const startOfYear = new Date(date.getFullYear(), 0, 1);
      const weekNum = Math.ceil(((date - startOfYear) / 604800000) + 1);
      return `${date.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
    }
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    case 'quarterly': {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${quarter}`;
    }
    default:
      return dateStr;
  }
};

const getGroupLabel = (key, groupBy) => {
  if (!key || key === 'Unknown') return 'Unknown';
  switch (groupBy) {
    case 'daily':
      return formatDateStr(key);
    case 'weekly':
      return key;
    case 'monthly': {
      const [year, month] = key.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    }
    case 'quarterly':
      return key;
    default:
      return key;
  }
};

const InvoiceProfitLoss = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('invoice-profit-loss');

  // Data states
  const [siTransactions, setSiTransactions] = useState(() => savedMem?.siTransactions ?? []);
  const [srTransactions, setSrTransactions] = useState(() => savedMem?.srTransactions ?? []);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedCustomer, setSelectedCustomer] = useState(() => savedMem?.selectedCustomer ?? '');
  const [selectedSalesperson, setSelectedSalesperson] = useState(() => savedMem?.selectedSalesperson ?? '');
  const [selectedJob, setSelectedJob] = useState(() => savedMem?.selectedJob ?? '');
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');
  const [selectedLocation, setSelectedLocation] = useState(() => savedMem?.selectedLocation ?? '');
  const [includeReturns, setIncludeReturns] = useState(() => savedMem?.includeReturns ?? true);
  const [taxFilter, setTaxFilter] = useState(() => savedMem?.taxFilter ?? 'both');
  const [groupBy, setGroupBy] = useState(() => savedMem?.groupBy ?? 'monthly');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');
  const [expandedRows, setExpandedRows] = useState(() => savedMem?.expandedRows ?? {});

  // Load master data
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(ref, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(ref, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.jobName != null)
        .sort((a, b) => String(a.jobName || '').localeCompare(String(b.jobName || ''))));
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  }, [activeCompany]);

  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations([...rows]);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadEmployees();
    loadJobs();
    loadRegions();
    loadAreas();
    loadLocations();

    // Backward memory: only default the dates on a fresh visit; on Back keep the
    // restored date range.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, loadCustomers, loadEmployees, loadJobs, loadRegions, loadAreas, loadLocations, savedMem]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Cascading areas
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Load transactions
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);
    setExpandedRows({});

    try {
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');

      // Load SI + PS
      const siQuery = query(ref,
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS']),
        where('financialYearId', '==', selectedFYId)
      );
      const siSnapshot = await getDocs(siQuery);
      let siList = siSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      siList = siList.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));
      setSiTransactions(siList);

      // Load SR
      if (includeReturns) {
        const srQuery = query(ref,
          where('tranTypeCode', 'in', ['SR', 'NSR']),
          where('financialYearId', '==', selectedFYId)
        );
        const srSnapshot = await getDocs(srQuery);
        let srList = srSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(t => {
            if (!activeBranch || activeBranch.id === 'all') return true;
            return t.branchId === activeBranch.id;
          });
        srList = srList.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));
        setSrTransactions(srList);
      } else {
        setSrTransactions([]);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Backward memory: remember this view (filters + loaded rows) once generated so
  // Back restores it with 0 re-reads. Filters are display-only over already-loaded
  // data, so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      siTransactions, srTransactions, hasSearched: true,
      selectedFYId, dateFrom, dateTo,
      selectedCustomer, selectedSalesperson, selectedJob,
      selectedRegionId, selectedAreaId, selectedLocation,
      includeReturns, taxFilter, groupBy,
      sortField, sortDirection, expandedRows
    });
  }, [hasSearched, siTransactions, srTransactions, selectedFYId, dateFrom, dateTo,
      selectedCustomer, selectedSalesperson, selectedJob, selectedRegionId, selectedAreaId,
      selectedLocation, includeReturns, taxFilter, groupBy, sortField, sortDirection,
      expandedRows, saveMemory]);

  // Build return map: SI id -> total return amount and cost
  const returnMap = useMemo(() => {
    const map = {};
    srTransactions.forEach(sr => {
      const parentId = sr.parentId;
      if (!parentId) return;
      if (!map[parentId]) {
        map[parentId] = { returnRevenue: 0, returnCost: 0 };
      }
      const lines = sr.lines || [];
      lines.forEach(line => {
        const qty = Number(line.quantity) || 0;
        map[parentId].returnRevenue += Number(line.lineTotal) || 0;
        map[parentId].returnCost += qty * (Number(line.unitCost) || 0);
      });
    });
    return map;
  }, [srTransactions]);

  // Process invoice data with profit calculations
  const invoiceProfitData = useMemo(() => {
    let filtered = [...siTransactions];

    // Date range
    if (dateFrom) filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    if (dateTo) filtered = filtered.filter(t => (t.docDate || '') <= dateTo);

    // Customer
    if (selectedCustomer) filtered = filtered.filter(t => t.partyId === selectedCustomer);

    // Salesperson
    if (selectedSalesperson) filtered = filtered.filter(t => t.employeeId === selectedSalesperson);

    // Job
    if (selectedJob) filtered = filtered.filter(t => t.jobId === selectedJob);

    // Location
    if (selectedLocation) filtered = filtered.filter(t => t.locationId === selectedLocation);

    // Region/Area
    if (selectedRegionId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.regionId === selectedRegionId;
      });
    }
    if (selectedAreaId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.areaId === selectedAreaId;
      });
    }

    // Tax filter
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => t.tranTypeCode !== 'NSI');
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => t.tranTypeCode !== 'SI');
    }

    return filtered.map(t => {
      const lines = t.lines || [];

      // Calculate revenue and cost from line items
      let revenue = 0;
      let cost = 0;
      const lineDetails = [];

      lines.forEach(line => {
        const qty = Number(line.quantity) || 0;
        const lineTotal = Number(line.lineTotal) || 0;
        const unitCost = Number(line.unitCost) || 0;
        const lineCost = qty * unitCost;
        const lineProfit = lineTotal - lineCost;
        const lineMargin = lineTotal !== 0 ? (lineProfit / lineTotal) * 100 : 0;

        revenue += lineTotal;
        cost += lineCost;

        lineDetails.push({
          itemName: line.itemName || line.description || '-',
          itemCode: line.itemCode || '',
          quantity: qty,
          unitPrice: Number(line.unitPrice) || 0,
          unitCost,
          lineRevenue: lineTotal,
          lineCost,
          lineProfit,
          lineMargin,
        });
      });

      const returns = returnMap[t.id] || { returnRevenue: 0, returnCost: 0 };
      const grossProfit = revenue - cost;
      const netProfit = grossProfit - returns.returnRevenue + returns.returnCost;
      const margin = revenue !== 0 ? (grossProfit / revenue) * 100 : 0;
      const netMargin = (revenue - returns.returnRevenue) !== 0
        ? (netProfit / (revenue - returns.returnRevenue)) * 100 : 0;

      return {
        id: t.id,
        docNumber: t.docNumber,
        docDate: t.docDate,
        partyName: t.partyName || '-',
        partyId: t.partyId,
        employeeName: t.employeeName || '-',
        status: t.status,
        tranTypeCode: t.tranTypeCode,
        itemsCount: lines.length,
        revenue,
        cost,
        grossProfit,
        margin,
        returnAmount: returns.returnRevenue,
        returnCost: returns.returnCost,
        netProfit,
        netMargin,
        lineDetails,
      };
    });
  }, [siTransactions, dateFrom, dateTo, selectedCustomer, selectedSalesperson, selectedJob, selectedLocation, selectedRegionId, selectedAreaId, customerMap, returnMap, taxFilter]);

  // Sort
  const sortedData = useMemo(() => {
    const sorted = [...invoiceProfitData];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [invoiceProfitData, sortField, sortDirection]);

  // Summary
  const summary = useMemo(() => {
    const totals = sortedData.reduce((acc, d) => {
      acc.revenue += d.revenue;
      acc.cost += d.cost;
      acc.grossProfit += d.grossProfit;
      acc.returnAmount += d.returnAmount;
      acc.netProfit += d.netProfit;
      return acc;
    }, { revenue: 0, cost: 0, grossProfit: 0, returnAmount: 0, netProfit: 0 });

    totals.margin = totals.revenue !== 0 ? (totals.grossProfit / totals.revenue) * 100 : 0;
    const netRev = totals.revenue - totals.returnAmount;
    totals.netMargin = netRev !== 0 ? (totals.netProfit / netRev) * 100 : 0;
    totals.invoiceCount = sortedData.length;
    return totals;
  }, [sortedData]);

  // Profit trend chart
  const trendData = useMemo(() => {
    const grouped = {};
    sortedData.forEach(d => {
      const key = getGroupKey(d.docDate, groupBy);
      if (!grouped[key]) {
        grouped[key] = { period: key, revenue: 0, cost: 0, profit: 0 };
      }
      grouped[key].revenue += d.revenue;
      grouped[key].cost += d.cost;
      grouped[key].profit += d.grossProfit;
    });

    return Object.values(grouped)
      .sort((a, b) => a.period.localeCompare(b.period))
      .map(g => ({ ...g, label: getGroupLabel(g.period, groupBy) }));
  }, [sortedData, groupBy]);

  // Top 10 invoices by profit
  const topInvoicesData = useMemo(() => {
    return [...sortedData]
      .sort((a, b) => b.grossProfit - a.grossProfit)
      .slice(0, 10)
      .map(d => ({
        name: d.docNumber.length > 18 ? d.docNumber.substring(0, 18) + '...' : d.docNumber,
        profit: Math.round(d.grossProfit),
      }));
  }, [sortedData]);

  // Handlers
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'docNumber' || field === 'partyName' || field === 'docDate' ? 'asc' : 'desc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  const toggleExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDocClick = (transaction) => {
    let route = '/sales/sale-invoice';
    if (transaction.tranTypeCode === 'PS') route = '/pos';
    navigate(route, { state: { viewTransactionId: transaction.id } });
  };

  // Export CSV
  const handleExport = () => {
    if (sortedData.length === 0) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Invoice-wise Profit & Loss Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Total Revenue,${summary.revenue}\n`;
    csv += `Total Cost,${summary.cost}\n`;
    csv += `Gross Profit,${summary.grossProfit}\n`;
    csv += `Total Returns,${summary.returnAmount}\n`;
    csv += `Net Profit,${summary.netProfit}\n`;
    csv += `Avg Margin,${summary.margin.toFixed(2)}%\n\n`;
    csv += 'Invoice #,Date,Customer,Salesperson,Items,Revenue,Cost,Gross Profit,Margin %,Returns,Net Profit,Net Margin %\n';

    sortedData.forEach(d => {
      csv += `"${d.docNumber}","${formatDateStr(d.docDate)}","${(d.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${(d.employeeName || '').replace(/"/g, '""')}",${d.itemsCount},`;
      csv += `${d.revenue},${d.cost},${d.grossProfit},${d.margin.toFixed(2)},`;
      csv += `${d.returnAmount},${d.netProfit},${d.netMargin.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_Profit_Loss_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Invoice-wise Profit & Loss</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .negative{color:#DC3545}
        .positive{color:#28A745}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Invoice-wise Profit & Loss Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)} |
        Invoices: ${sortedData.length}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total Revenue</div><div class="summary-val">${formatAmount(summary.revenue)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Cost</div><div class="summary-val">${formatAmount(summary.cost)}</div></div>
        <div class="summary-box"><div class="summary-label">Gross Profit</div><div class="summary-val ${summary.grossProfit >= 0 ? 'positive' : 'negative'}">${formatAmount(summary.grossProfit)}</div></div>
        <div class="summary-box"><div class="summary-label">Returns</div><div class="summary-val">${formatAmount(summary.returnAmount)}</div></div>
        <div class="summary-box"><div class="summary-label">Net Profit</div><div class="summary-val ${summary.netProfit >= 0 ? 'positive' : 'negative'}">${formatAmount(summary.netProfit)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Margin</div><div class="summary-val">${summary.margin.toFixed(2)}%</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Invoice #</th><th>Date</th><th>Customer</th><th>Salesperson</th>
          <th class="right">Items</th><th class="right">Revenue</th><th class="right">Cost</th>
          <th class="right">Gross Profit</th><th class="right">Margin %</th>
          <th class="right">Returns</th><th class="right">Net Profit</th>
        </tr></thead>
        <tbody>
          ${sortedData.map(d => `
            <tr>
              <td>${d.docNumber || '-'}</td>
              <td>${formatDateStr(d.docDate)}</td>
              <td>${d.partyName}</td>
              <td>${d.employeeName}</td>
              <td class="right">${d.itemsCount}</td>
              <td class="right">${formatAmount(d.revenue)}</td>
              <td class="right">${formatAmount(d.cost)}</td>
              <td class="right ${d.grossProfit >= 0 ? 'positive' : 'negative'}">${formatAmount(d.grossProfit)}</td>
              <td class="right ${d.margin >= 0 ? 'positive' : 'negative'}">${d.margin.toFixed(2)}%</td>
              <td class="right">${formatAmount(d.returnAmount)}</td>
              <td class="right ${d.netProfit >= 0 ? 'positive' : 'negative'}">${formatAmount(d.netProfit)}</td>
            </tr>
          `).join('')}
          <tr class="totals-row">
            <td colspan="5" style="text-align:right">Totals (${sortedData.length} invoices)</td>
            <td class="right">${formatAmount(summary.revenue)}</td>
            <td class="right">${formatAmount(summary.cost)}</td>
            <td class="right ${summary.grossProfit >= 0 ? 'positive' : 'negative'}">${formatAmount(summary.grossProfit)}</td>
            <td class="right">${summary.margin.toFixed(2)}%</td>
            <td class="right">${formatAmount(summary.returnAmount)}</td>
            <td class="right ${summary.netProfit >= 0 ? 'positive' : 'negative'}">${formatAmount(summary.netProfit)}</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Invoice-wise Profit & Loss" breadcrumb="Reports / Sale Reports / Invoice-wise Profit & Loss">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={selectedSalesperson} onChange={e => setSelectedSalesperson(e.target.value)}>
              <option value="">All Salespersons</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeCode ? `${emp.employeeCode} - ` : ''}{emp.fullName || emp.firstName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Job</Label>
            <Select value={selectedJob} onChange={e => setSelectedJob(e.target.value)}>
              <option value="">All Jobs</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.jobCode ? `${j.jobCode} - ` : ''}{j.jobName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Location</Label>
            <Select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)}>
              <option value="">All Locations</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.locationName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Include Returns</Label>
            <Select value={includeReturns ? 'yes' : 'no'} onChange={e => setIncludeReturns(e.target.value === 'yes')}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Trend Group By</Label>
            <Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedData.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedData.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Revenue</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatCurrency(summary.revenue)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Cost</SummaryLabel>
              <SummaryValue style={{ color: '#DC3545' }}>{formatCurrency(summary.cost)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Gross Profit</SummaryLabel>
              <SummaryValue style={{ color: summary.grossProfit >= 0 ? '#28A745' : '#DC3545' }}>
                {formatCurrency(summary.grossProfit)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#F57C00"><FiTrendingDown /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Returns</SummaryLabel>
              <SummaryValue style={{ color: '#F57C00' }}>{formatCurrency(summary.returnAmount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Net Profit</SummaryLabel>
              <SummaryValue style={{ color: summary.netProfit >= 0 ? '#28A745' : '#DC3545' }}>
                {formatCurrency(summary.netProfit)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Margin</SummaryLabel>
              <SummaryValue>{summary.margin.toFixed(2)}%</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiFileText /></EmptyIcon>
              <EmptyText>Loading invoice profit data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiFileText /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view invoice-wise profit & loss</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiFileText /></EmptyIcon>
              <EmptyText>No invoices found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 30 }}></Th>
                  <Th $sortable onClick={() => handleSort('docNumber')}>Invoice #{getSortIndicator('docNumber')}</Th>
                  <Th $sortable onClick={() => handleSort('docDate')}>Date{getSortIndicator('docDate')}</Th>
                  <Th $sortable onClick={() => handleSort('partyName')}>Customer{getSortIndicator('partyName')}</Th>
                  <Th $sortable onClick={() => handleSort('employeeName')}>Salesperson{getSortIndicator('employeeName')}</Th>
                  <Th $sortable $right onClick={() => handleSort('itemsCount')}>Items{getSortIndicator('itemsCount')}</Th>
                  <Th $sortable $right onClick={() => handleSort('revenue')}>Revenue{getSortIndicator('revenue')}</Th>
                  <Th $sortable $right onClick={() => handleSort('cost')}>Cost{getSortIndicator('cost')}</Th>
                  <Th $sortable $right onClick={() => handleSort('grossProfit')}>Gross Profit{getSortIndicator('grossProfit')}</Th>
                  <Th $sortable $right onClick={() => handleSort('margin')}>Margin %{getSortIndicator('margin')}</Th>
                  {includeReturns && <Th $sortable $right onClick={() => handleSort('returnAmount')}>Returns{getSortIndicator('returnAmount')}</Th>}
                  {includeReturns && <Th $sortable $right onClick={() => handleSort('netProfit')}>Net Profit{getSortIndicator('netProfit')}</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {sortedData.map((d, index) => (
                  <React.Fragment key={d.id || index}>
                    <Tr>
                      <Td style={{ cursor: 'pointer', padding: '10px 8px' }} onClick={() => toggleExpand(d.id)}>
                        {expandedRows[d.id] ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      </Td>
                      <Td>
                        <DocNumber onClick={() => handleDocClick(d)} title="Click to open invoice">
                          {d.docNumber || '-'} <FiExternalLink size={11} />
                        </DocNumber>
                      </Td>
                      <Td>{formatDateStr(d.docDate)}</Td>
                      <Td>{d.partyName}</Td>
                      <Td>{d.employeeName}</Td>
                      <AmountCell $right>{d.itemsCount}</AmountCell>
                      <AmountCell $right>{formatAmount(d.revenue)}</AmountCell>
                      <AmountCell $right>{formatAmount(d.cost)}</AmountCell>
                      <AmountCell $right $type={d.grossProfit >= 0 ? 'sale' : 'return'}>
                        {formatAmount(d.grossProfit)}
                      </AmountCell>
                      <MarginCell $value={d.margin}>{d.margin.toFixed(2)}%</MarginCell>
                      {includeReturns && (
                        <AmountCell $right $type={d.returnAmount > 0 ? 'return' : undefined}>
                          {d.returnAmount > 0 ? formatAmount(d.returnAmount) : '-'}
                        </AmountCell>
                      )}
                      {includeReturns && (
                        <AmountCell $right $type={d.netProfit >= 0 ? 'sale' : 'return'}>
                          {formatAmount(d.netProfit)}
                        </AmountCell>
                      )}
                    </Tr>

                    {/* Expanded Line Details */}
                    {expandedRows[d.id] && d.lineDetails.length > 0 && (
                      <ExpandedRow>
                        <td colSpan={includeReturns ? 13 : 11}>
                          <LineTable>
                            <thead>
                              <tr>
                                <LineTh>Item</LineTh>
                                <LineTh $right>Qty</LineTh>
                                <LineTh $right>Unit Price</LineTh>
                                <LineTh $right>Unit Cost</LineTh>
                                <LineTh $right>Revenue</LineTh>
                                <LineTh $right>Cost</LineTh>
                                <LineTh $right>Profit</LineTh>
                                <LineTh $right>Margin %</LineTh>
                              </tr>
                            </thead>
                            <tbody>
                              {d.lineDetails.map((line, li) => (
                                <tr key={li}>
                                  <LineTd>
                                    {line.itemCode ? <span style={{ color: colors.text.secondary, fontSize: 11 }}>{line.itemCode} - </span> : ''}
                                    {line.itemName}
                                  </LineTd>
                                  <LineTd $right>{line.quantity}</LineTd>
                                  <LineTd $right>{formatAmount(line.unitPrice)}</LineTd>
                                  <LineTd $right>{formatAmount(line.unitCost)}</LineTd>
                                  <LineTd $right>{formatAmount(line.lineRevenue)}</LineTd>
                                  <LineTd $right>{formatAmount(line.lineCost)}</LineTd>
                                  <LineTd $right style={{ color: line.lineProfit >= 0 ? '#28A745' : '#DC3545', fontWeight: 500 }}>
                                    {formatAmount(line.lineProfit)}
                                  </LineTd>
                                  <LineTd $right style={{ color: line.lineMargin >= 0 ? '#28A745' : '#DC3545' }}>
                                    {line.lineMargin.toFixed(2)}%
                                  </LineTd>
                                </tr>
                              ))}
                            </tbody>
                          </LineTable>
                        </td>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                ))}

                <TotalsRow>
                  <Td></Td>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedData.length} invoices)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(summary.revenue)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(summary.cost)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700, color: summary.grossProfit >= 0 ? '#28A745' : '#DC3545' }}>
                    {formatAmount(summary.grossProfit)}
                  </AmountCell>
                  <MarginCell $value={summary.margin} style={{ fontWeight: 700 }}>{summary.margin.toFixed(2)}%</MarginCell>
                  {includeReturns && (
                    <AmountCell $right style={{ fontWeight: 700, color: '#DC3545' }}>{formatAmount(summary.returnAmount)}</AmountCell>
                  )}
                  {includeReturns && (
                    <AmountCell $right style={{ fontWeight: 700, color: summary.netProfit >= 0 ? '#28A745' : '#DC3545' }}>
                      {formatAmount(summary.netProfit)}
                    </AmountCell>
                  )}
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && sortedData.length > 0 && (
        <ChartsRow>
          <ChartSection $wide>
            <ChartTitle>Profit Trend ({groupBy.charAt(0).toUpperCase() + groupBy.slice(1)})</ChartTitle>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#28A745" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#28A745" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC3545" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#DC3545" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#28A745" fill="url(#revGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="cost" name="Cost" stroke="#DC3545" fill="url(#costGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="profit" name="Profit" stroke={colors.primary.main} fill="url(#profitGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No trend data available</ChartEmpty>
            )}
          </ChartSection>

          <ChartSection>
            <ChartTitle>Top 10 Invoices by Profit</ChartTitle>
            {topInvoicesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topInvoicesData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} stroke={colors.text.secondary} width={130} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="profit" name="Gross Profit" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const ExpandedRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};

  > td {
    padding: 12px 20px 16px 50px;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => {
    if (props.$type === 'return') return '#DC3545';
    if (props.$type === 'sale') return '#28A745';
    return colors.text.primary;
  }};
`;

const MarginCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  font-weight: 500;
  color: ${props => {
    const v = props.$value || 0;
    if (v >= 20) return '#28A745';
    if (v >= 10) return '#F57C00';
    if (v >= 0) return '#FF9800';
    return '#DC3545';
  }};
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.5;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const LineTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: ${colors.borderRadius.md};
  overflow: hidden;
  box-shadow: ${colors.shadows.sm};
`;

const LineTh = styled.th`
  padding: 8px 12px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 10px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const LineTd = styled.td`
  padding: 6px 12px;
  font-size: 12px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
  border-bottom: 1px solid ${colors.neutral[100]};
  font-family: ${props => props.$right ? "'SF Mono', 'Consolas', monospace" : 'inherit'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default InvoiceProfitLoss;
